import React from 'react'
import ReactMarkdown from 'react-markdown'
import { safeURL } from './safeURL'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import makeStyles from '@mui/styles/makeStyles'
import AppLink from './AppLink'
import timestampSupport from './Markdown.timestampSupport'

const useStyles = makeStyles((theme) => ({
  markdown: {
    overflowWrap: 'break-word',
    '& pre': {
      padding: '0.375rem',
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.default,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: '4px',
    },
    '& code': {
      padding: '2px 4px',
      fontSize: '90%',
      color: theme.palette.secondary.main,
      backgroundColor: theme.palette.action.selected,
      borderRadius: '4px',
    },
    '& pre code': {
      padding: 0,
      color: 'inherit',
      whiteSpace: 'pre-wrap',
      backgroundColor: 'inherit',
      borderRadius: 0,
    },
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    borderSpacing: 0,
    marginBottom: '1rem',
    border: `1px solid ${theme.palette.divider}`,
  },
  row: {
    '&:nth-of-type(even)': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  cell: {
    textAlign: 'left',
    padding: '0.5rem 1rem',
    border: `1px solid ${theme.palette.divider}`,
  },
  headerCell: {
    backgroundColor: theme.palette.background.default,
    fontWeight: 600,
    borderBottom: `2px solid ${theme.palette.divider}`,
  },
}))

function TableCell({ children, className, isHeader, align, ...rest }) {
  const content = React.Children.map(children, (c) =>
    ['<br>', '<br/>', '<br />'].includes(c) ? <br /> : c,
  )

  return (
    <td className={className} style={{ textAlign: align }} {...rest}>
      {content}
    </td>
  )
}

// Markdown accepts plain text to transform into styled html
// Typically it is wrapped in a <Typography component='div' /> component
export default function Markdown(props) {
  const classes = useStyles()
  const { value, ...rest } = props
  if (!value) return null

  return (
    <ReactMarkdown
      className={classes.markdown}
      components={{
        table: ({ children, ...props }) => (
          <table className={classes.table} {...props}>
            {children}
          </table>
        ),
        tr: ({ children, ...props }) => (
          <tr className={classes.row} {...props}>
            {children}
          </tr>
        ),
        th: ({ children, ...props }) => (
          <th
            className={`${classes.cell} ${classes.headerCell}`}
            {...props}
          >
            {children}
          </th>
        ),
        td: ({ children, align, ...props }) => (
          <TableCell
            className={classes.cell}
            align={align}
            {...props}
          >
            {children}
          </TableCell>
        ),
        a: ({ node, inline, className, children, ...props }) => {
          if (props.href && props.href.startsWith('/users/')) {
            return (
              <AppLink
                to={props.href}
                {...props}
                sx={{
                  display: 'inline-block',
                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                  padding: '2px 6px',
                  borderRadius: '16px', // Chip-like rounded corners
                  fontWeight: 500,
                  fontSize: '0.8125rem', // Matches typical chip font size
                  textDecoration: 'none',
                  color: 'text.primary',
                  lineHeight: '1.5',
                  transition: 'background-color 0.2s',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.12)',
                  },
                  // Dark mode overrides
                  '.theme-dark &': {
                    backgroundColor: 'rgba(255, 255, 255, 0.12)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    },
                  },
                  '@media (prefers-color-scheme: dark)': {
                    backgroundColor: 'rgba(255, 255, 255, 0.12)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    },
                  },
                }}
              >
                {children}
              </AppLink>
            )
          }
          return (
            <AppLink to={props.href} newTab {...props}>
              {children}
            </AppLink>
          )
        },
      }}
      remarkPlugins={[timestampSupport, remarkGfm, remarkBreaks]}
      allowElement={(element) => {
        if (
          element.tagName === 'a' &&
          element.children[0].type === 'text' &&
          !safeURL(element.properties.href, element.children[0].value)
        ) {
          element.type = 'text'
          element.value = `[${element.children[0].value}](${element.properties.href})`
          delete element.properties.href
        }

        return true
      }}
      {...rest}
    >
      {value}
    </ReactMarkdown>
  )
}
